import React from 'react';
import {
  FolderOpen,
  FileText,
  Share2,
  Mail,
  Image,
  Mic,
  Globe,
  MoreHorizontal,
  Plus,
  Eye,
  EyeOff,
} from 'lucide-react';
import type { MarketingRequestStatus, MarketingRequestType } from '../../types/marketing';
import './MarketingSidebar.css';

interface MarketingRequestSidebarProps {
  selectedType: MarketingRequestType | null;
  selectedStatus: MarketingRequestStatus | null;
  onSelectType: (t: MarketingRequestType | null) => void;
  onSelectStatus: (s: MarketingRequestStatus | null) => void;
  typeCounts: Record<string, number>;
  statusCounts: Record<string, number>;
  hideClosed: boolean;
  onToggleHideClosed: () => void;
  onNewRequest: () => void;
  canCreate: boolean;
}

const TYPES: { id: MarketingRequestType | null; label: string; icon: React.ReactNode }[] = [
  { id: null, label: 'All types', icon: <FolderOpen size={15} /> },
  { id: 'content', label: 'Content', icon: <FileText size={15} /> },
  { id: 'social', label: 'Social', icon: <Share2 size={15} /> },
  { id: 'email', label: 'Email', icon: <Mail size={15} /> },
  { id: 'graphic', label: 'Graphic', icon: <Image size={15} /> },
  { id: 'podcast', label: 'Podcast', icon: <Mic size={15} /> },
  { id: 'website', label: 'Website', icon: <Globe size={15} /> },
  { id: 'other', label: 'Other', icon: <MoreHorizontal size={15} /> },
];

const STATUSES: { id: MarketingRequestStatus | null; label: string }[] = [
  { id: null, label: 'All statuses' },
  { id: 'open', label: 'Open' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'done', label: 'Done' },
  { id: 'cancelled', label: 'Cancelled' },
];

const MarketingRequestSidebar: React.FC<MarketingRequestSidebarProps> = ({
  selectedType,
  selectedStatus,
  onSelectType,
  onSelectStatus,
  typeCounts,
  statusCounts,
  hideClosed,
  onToggleHideClosed,
  onNewRequest,
  canCreate,
}) => (
  <aside className="mh-sidebar">
    <div className="mh-sidebar-header">
      <span className="mh-sidebar-label">Content Requests</span>
    </div>

    {canCreate && (
      <button type="button" className="mh-sidebar-new-btn" onClick={onNewRequest}>
        <Plus size={15} />
        New request
      </button>
    )}

    <nav className="mh-sidebar-nav" aria-label="Request types">
      {TYPES.map(item => {
        const isActive = item.id === selectedType;
        const count = item.id === null ? typeCounts.all : typeCounts[item.id] || 0;
        return (
          <button
            key={item.id ?? 'all-types'}
            type="button"
            className={`mh-sidebar-item ${isActive ? 'active' : ''}`}
            onClick={() => onSelectType(item.id)}
            aria-pressed={isActive}
          >
            <span className="mh-sidebar-item-icon">{item.icon}</span>
            <span className="mh-sidebar-item-label">{item.label}</span>
            {count > 0 && <span className="mh-sidebar-item-count">{count}</span>}
          </button>
        );
      })}
    </nav>

    <div className="mh-sidebar-divider" aria-hidden />

    <nav className="mh-sidebar-nav" aria-label="Request status">
      {STATUSES.map(item => {
        const isActive = item.id === selectedStatus;
        const count = item.id === null ? statusCounts.all : statusCounts[item.id] || 0;
        return (
          <button
            key={item.id ?? 'all-statuses'}
            type="button"
            className={`mh-sidebar-item ${isActive ? 'active' : ''}`}
            onClick={() => onSelectStatus(item.id)}
            aria-pressed={isActive}
          >
            <span className="mh-sidebar-item-label">{item.label}</span>
            {count > 0 && <span className="mh-sidebar-item-count">{count}</span>}
          </button>
        );
      })}
    </nav>

    <button
      type="button"
      className={`mh-sidebar-toggle-archived ${!hideClosed ? 'showing' : ''}`}
      onClick={onToggleHideClosed}
    >
      {hideClosed ? <Eye size={13} /> : <EyeOff size={13} />}
      <span>{hideClosed ? 'Show closed' : 'Hide closed'}</span>
    </button>
  </aside>
);

export default MarketingRequestSidebar;
